import * as React from "react";
import { CaseRunClient, CaseRunDetailReadModel, CaseRunDetailResponseWithUrls } from "@app/api2/api";
import { Dropdown, DropdownItem, DropdownMenu, Label, Dimmer, Loader, MenuItem } from "@/components/bp/bp-ui";
import { replaceInString } from "@common/Utils";
import { Link } from "react-router-dom";
import { PageHeader } from "@common/PageHeader";
import { areYouSure } from "../../AreYouSure";
import { CaseRunPrintDialog } from "../Print/CaseRunPrintDialog";
import { buildUrl, AppRoutes } from "../../AppRoutes";
import { CaseRunDetailsPane } from "./CaseRunDetailsPane";
import { RoutedTab } from "./RoutedTab";
import { MaxLrCaseRunResults } from "./Results/MaxLR/MaxLrCaseRunResults";
import { BPTabPane } from "@common/BPTabPane";
import { HandlerRegistry } from "../RunTypes/HandlerRegistry";
import { TranslationContext } from "../../Contexts/TranslationContext";
import { BPButton } from "@/components/bp/bpbutton";
import { BPContainer } from "@/components/bp/bpcontainer";


export interface CaseRunDetailsProps {
    caseRunId: number;
    caseId: number;
    labId: number;
}

class CaseRunDetailsState {
    public model: CaseRunDetailReadModel;
    res: CaseRunDetailResponseWithUrls;
    showPrintDialog: boolean = false;
}

export class CaseRunDetails extends React.Component<CaseRunDetailsProps, CaseRunDetailsState> {


    private caseRunClient = new CaseRunClient();

    constructor(props, context) {
        super(props, context);

        this.state = new CaseRunDetailsState();
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prev: CaseRunDetailsProps) {
        if (prev.caseRunId != this.props.caseRunId) {
            this.loadData();
        }
    }

    async loadData() {
        let res = await this.caseRunClient.detail(this.props.labId, this.props.caseId, this.props.caseRunId);

        // // replace urls
        // for (let key in Object.keys(res.evidenceUrls)) {
        //     res.evidenceUrls[key] = buildUrl(Routes.EvidenceDetails, { caseId: this.props.caseId, evidenceId: key, labId: this.props.labId })
        // }

        // for (let key in Object.keys(res.referenceUrls)) {
        //     res.referenceUrls[key] = buildUrl(Routes.SampleDetails, { caseId: this.props.caseId, sampleId: key, labId: this.props.labId })
        // }

        this.setState({
            model: res.detail,
            res: res,
        });
    }

    @areYouSure(() => { return { message: window.translation.caseRunView.deleteConfirm, title: window.translation.common.confirmDelete }; })
    async deleteCaseRun() {
        await this.caseRunClient.delete(this.props.labId, this.props.caseId, this.props.caseRunId);
        window.location.reload()
    }

    render() {
        const t = window.translation;
        // const handler = HandlerRegistry.getRunTypeHandler(this.state.model?.runType)

        const title = replaceInString(t.caseRunView.caseRunDetails, {
            '{runId}': this.props.caseRunId,
        });

        if (!this.state.model)
            return (<TranslationContext.Consumer>{t =>
                <Dimmer inverted active={true}>
                    <Loader inverted>{t.caseRun.loadingData}</Loader>
                </Dimmer>
            }</TranslationContext.Consumer>);

        return (
            <TranslationContext.Consumer>{t =>
                <BPContainer>
                    {/* <Segment> */}
                    <PageHeader
                        header={<>
                            {title}
                            {this.state.model.isDeleted &&
                                <Label as='div' color='orange' size={'tiny'} ribbon={false}>
                                    {window.translation.caseRunView.deletedMessage}
                                </Label>
                            }
                        </>}
                        documentTitle={title}
                        subheader={<a href={this.state.res.caseUrl}>{this.state.model.caseName}</a>}
                        renderRight={() => {
                            return (
                                <>
                                    {!this.state.model.isReadOnly && !this.state.model.isDeleted && this.state.model.isCompleted && this.state.model.canCreateRunTypes.length > 0 &&
                                        <MenuItem fitted>
                                            <Dropdown floating text={t.caseRunView.calculations} button>
                                                <DropdownMenu>
                                                    {this.state.model.canCreateRunTypes.length &&
                                                        this.state.model.canCreateRunTypes.map(rt => {
                                                            return (<DropdownItem key={rt.runType} as={Link} to={buildUrl(AppRoutes.CaseRunCalculate, { caseId: this.props.caseId.toString(), labId: this.props.labId.toString(), caseRunId: this.props.caseRunId.toString(), runType: rt.runType })}>
                                                                {t.runType[rt.runType]}
                                                            </DropdownItem>)
                                                        })
                                                    }
                                                </DropdownMenu>
                                            </Dropdown>
                                        </MenuItem>
                                    }
                                    {!this.state.model.isReadOnly && <MenuItem fitted>
                                        <BPButton as={Link} to={buildUrl(AppRoutes.CaseRunClone, { caseId: this.props.caseId.toString(), labId: this.props.labId.toString(), oldRunId: this.props.caseRunId.toString(), runType: this.state.model.runType })}>{t.caseRunView.clone}</BPButton>
                                    </MenuItem>}
                                    {this.state.model.isCompleted && <>
                                        <MenuItem fitted>
                                            <BPButton onClick={() => this.setState({ showPrintDialog: true })}>{t.caseRunView.print}</BPButton>
                                        </MenuItem>
                                        <MenuItem fitted>
                                            <BPButton as="a" href={this.state.res.exportUrl}>{t.caseRunView.export}</BPButton>
                                        </MenuItem>
                                    </>
                                    }
                                    {!this.state.model.isReadOnly && !this.state.model.isDeleted &&
                                        <MenuItem fitted>
                                            <BPButton negative onClick={() => this.deleteCaseRun()}>{t.common.delete}</BPButton>
                                        </MenuItem>
                                    }
                                </>
                            )
                        }}
                    />

                    <CaseRunPrintDialog open={this.state.showPrintDialog}
                        options={this.state.model.caseRunPrintOptions}
                        onClose={() => this.setState({ showPrintDialog: false })}
                        onOpen={() => this.setState({ showPrintDialog: true })}
                        onOk={values => {
                            window.sessionStorage.setItem(`print${this.props.caseRunId}`, JSON.stringify(values))
                            window.open(buildUrl(AppRoutes.CaseRunPrint, {
                                caseId: this.props.caseId.toString(),
                                labId: this.props.labId.toString(),
                                caseRunId: this.props.caseRunId.toString()
                            }), '_blank');
                        }} />

                    {/* <Routes>
                        <Route path={':calculationId/detail'} element={<RouteContent render={routeProps => <CalculationDetailsModal header={t.caseRunView.calculationDetails} closeButtonLabel={t.common.close} goBack={() => routeProps.navigate(buildUrl(AppRoutes.CaseRunDetails, {
                            caseId: this.props.caseId.toString(),
                            caseRunId: this.props.caseRunId.toString(),
                            labId: this.props.labId.toString(),
                        }))} calculationId={parseInt(routeProps.params.calculationId)} caseId={this.props.caseId} labId={this.props.labId} caseRunId={this.props.caseRunId} />} />} />
                    </Routes> */}


                    <RoutedTab tabs={[
                        {
                            path: ["/", "calculations/*"],
                            exact: true,
                            enabled: true,
                            to: buildUrl(AppRoutes.CaseRunDetails, {
                                caseId: this.props.caseId.toString(),
                                caseRunId: this.props.caseRunId.toString(),
                                labId: this.props.labId.toString()
                            }),
                            menuItem: t.caseRunView.information,
                            render: () => <BPTabPane>
                                <CaseRunDetailsPane model={this.state.model} referenceUrls={this.state.res.referenceUrls} evidenceUrls={this.state.res.evidenceUrls} caseUrl={this.state.res.caseUrl} populationUrl={this.state.res.populationUrl} />
                            </BPTabPane>
                        },
                        // ...this.state.model.isCompleted ? [
                        {
                            path: 'results',
                            menuItem: t.common.results,
                            exact: true,
                            enabled: this.state.model.isCompleted,
                            to: '../results',
                            // to: buildUrl(Routes.CaseRunDetailsResults, {
                            //     caseId: this.props.caseId.toString(),
                            //     caseRunId: this.props.caseRunId.toString(),
                            //     labId: this.props.labId.toString()
                            // }),
                            render: () => <BPTabPane>{HandlerRegistry.renderResults(this.state.model.runType, this.props) ?? <MaxLrCaseRunResults caseId={this.props.caseId} labId={this.props.labId} caseRunId={this.props.caseRunId} />}
                            </BPTabPane>
                        }//] : [],
                    ]} />
                    {/* </Segment> */}
                </BPContainer>
            }</TranslationContext.Consumer>)
    }
}



